import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Line } from "react-chartjs-2";

import {
  baseOptions,
  barDataset,
  endLabels,
  GENDER_COLORS,
  insideLabels,
  MIN_BAR,
  INK,
  REASON_COLORS,
  REASON_ORDER,
  SERIES,
  trendLabels,
} from "./chartTheme";
import { Empty } from "./StatCard";

const PLUGINS = [ChartDataLabels];

/** 24 monthly labels do not fit unrotated; let Chart.js thin them. */
const compactMonthAxis = (options) => {
  options.scales.x.ticks = { ...options.scales.x.ticks, autoSkip: true, maxTicksLimit: 12, maxRotation: 0 };
};

const Frame = ({ height = 260, children, label }) => (
  <div className="ws-chart" style={{ height }} role="img" aria-label={label}>
    {children}
  </div>
);

/** Hires vs leavers per month: two series, one axis, side by side. */
export const HiresLeaversChart = ({ trend }) => {
  if (!trend?.length) return <Empty />;
  const data = {
    labels: trendLabels(trend),
    datasets: [
      barDataset("Hires", trend.map((m) => m.hires), SERIES.primary),
      barDataset("Leavers", trend.map((m) => m.leavers), SERIES.secondary),
    ],
  };
  return (
    <Frame label="Hires and leavers per month">
      <Bar data={data} options={baseOptions({ legend: true })} />
    </Frame>
  );
};

/** Monthly turnover %, a single line: a legend would only repeat the title. */
export const TurnoverRateChart = ({ trend }) => {
  if (!trend?.length) return <Empty />;
  const options = baseOptions({ percent: true });
  options.plugins.tooltip = {
    ...options.plugins.tooltip,
    callbacks: {
      label: (ctx) => {
        const month = trend[ctx.dataIndex];
        return ` ${ctx.parsed.y ?? 0}% (${month.leavers} leavers / ~${month.headcount} staff)`;
      },
    },
  };
  const data = {
    labels: trendLabels(trend),
    datasets: [
      {
        label: "Turnover rate",
        data: trend.map((m) => m.turnover_rate ?? 0),
        borderColor: SERIES.primary,
        backgroundColor: "rgba(42, 120, 214, 0.08)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: SERIES.primary,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
      },
    ],
  };
  return (
    <Frame label="Monthly turnover rate">
      <Line data={data} options={options} />
    </Frame>
  );
};

/** Why people left, as a ranked list of bars with count and share. */
export const ReasonBars = ({ reasons }) => {
  const total = (reasons || []).reduce((sum, r) => sum + r.count, 0);
  if (!total) return <Empty>No leavers in this period.</Empty>;
  const options = baseOptions({ horizontal: true });
  options.layout = { padding: { right: 64 } };
  options.plugins.datalabels = endLabels((v) => `${v} · ${Math.round((v * 100) / total)}%`);
  options.scales.x.display = false;
  const data = {
    labels: reasons.map((r) => r.name),
    datasets: [
      barDataset("Leavers", reasons.map((r) => r.count), reasons.map((r) => REASON_COLORS[r.name] || INK.muted), {
        borderSkipped: "start",
        maxBarThickness: 22,
      }),
    ],
  };
  return (
    <Frame height={Math.max(150, reasons.length * 44)} label="Leavers by reason">
      <Bar data={data} options={options} plugins={PLUGINS} />
    </Frame>
  );
};

/** Leavers by how long they had been in the job, stacked by reason. */
/** Stacked-by-reason bar chart over any set of buckets (time-in-job bands,
 *  months). `reasonsOf(bucket)` returns its { reasonLabel: count } map. */
const ReasonStack = ({ buckets, labels, reasonsOf, label, height, monthAxis = false }) => {
  const present = REASON_ORDER.filter((reason) => buckets.some((b) => reasonsOf(b)[reason]));
  const data = {
    labels,
    datasets: present.map((reason) =>
      barDataset(reason, buckets.map((b) => reasonsOf(b)[reason] || 0), REASON_COLORS[reason], {
        borderWidth: { top: 2, bottom: 0, left: 0, right: 0 },
        borderSkipped: false,
        borderRadius: 0,
      })
    ),
  };
  const options = baseOptions({ stacked: true, legend: present.length > 1 });
  options.plugins.tooltip = { ...options.plugins.tooltip, filter: (item) => item.parsed.y > 0 };
  if (monthAxis) compactMonthAxis(options);
  return (
    <Frame label={label} height={height}>
      <Bar data={data} options={options} />
    </Frame>
  );
};

export const EarlyAttritionChart = ({ bands }) => {
  if (!bands?.some((b) => b.total)) return <Empty>No leavers in this period.</Empty>;
  return (
    <ReasonStack
      buckets={bands}
      labels={bands.map((b) => b.band)}
      reasonsOf={(b) => b.by_reason}
      label="Leavers by time in job and reason"
    />
  );
};

/** Wide charts scroll sideways inside their card, as the original ones did. */
export const ScrollX = ({ count, perItem, height = 300, label, children }) => (
  <div className="ws-scroll-x" role="img" aria-label={label}>
    <div style={{ minWidth: Math.max(count * perItem, 320), height, position: "relative" }}>{children}</div>
  </div>
);

/** One line per series over months, every month shown (the chart scrolls
 *  sideways when needed) and each non-zero point labelled, as the original
 *  month charts were. `series`: [{ label, color, valueOf(month) }]. */
export const MonthLines = ({ trend, series, label, perMonth = 56 }) => {
  if (!trend?.length) return <Empty />;
  const options = baseOptions({ legend: series.length > 1 });
  options.layout = { padding: { top: 22, right: 12 } };
  options.scales.x.ticks = { ...options.scales.x.ticks, autoSkip: false, maxRotation: 0 };
  options.plugins.datalabels = {
    display: (ctx) => (ctx.dataset.data[ctx.dataIndex] || 0) !== 0,
    align: "top",
    offset: 4,
    color: INK.secondary,
    font: { family: "'Inter', sans-serif", size: 10, weight: "600" },
  };
  const data = {
    labels: trendLabels(trend),
    datasets: series.map((s) => ({
      label: s.label,
      data: trend.map(s.valueOf),
      borderColor: s.color,
      backgroundColor: s.color,
      borderWidth: 2,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
    })),
  };
  return (
    <ScrollX count={trend.length} perItem={perMonth} height={300} label={label}>
      <Line data={data} options={options} plugins={PLUGINS} />
    </ScrollX>
  );
};

/** The old "Resigned/Terminated/Non-Renewal" card, still a line chart: one
 *  line per BC reason, leavers per month. */
export const MonthlyLeaversChart = ({ trend }) => {
  if (!trend?.some((m) => m.leavers)) return <Empty>No leavers in this period.</Empty>;
  const present = REASON_ORDER.filter((reason) => trend.some((m) => m.leavers_by_reason[reason]));
  return (
    <MonthLines
      trend={trend}
      label="Leavers per month by reason"
      series={present.map((reason) => ({
        label: reason,
        color: REASON_COLORS[reason],
        valueOf: (m) => m.leavers_by_reason[reason] || 0,
      }))}
    />
  );
};

/** A single-series count chart with value labels (tenure, grade, span). */
export const CountBars = ({ items, labelKey = "band", horizontal = false, height, color = SERIES.primary, label }) => {
  if (!items?.some((i) => i.count)) return <Empty />;
  const options = baseOptions({ horizontal });
  options.plugins.datalabels = insideLabels();
  if (horizontal) options.scales.x.display = false;
  const data = {
    labels: items.map((i) => i[labelKey]),
    // Zero stays zero (null draws nothing); anything else is at least
    // MIN_BAR long so its label fits inside.
    datasets: [
      barDataset(label, items.map((i) => i.count || null), color, {
        minBarLength: horizontal ? MIN_BAR + 8 : MIN_BAR,
      }),
    ],
  };
  return (
    <Frame height={height || (horizontal ? Math.max(160, items.length * 36) : 240)} label={label}>
      <Bar data={data} options={options} plugins={PLUGINS} />
    </Frame>
  );
};

/** Age × gender pyramid: men to the left, women to the right, one shared
 *  scale. "Other / not recorded" is reported in text, not drawn, so the two
 *  sides stay comparable. */
/** Axis half-width for the pyramid: the longest bar plus room for its label,
 *  rounded up to a readable step. */
const PYRAMID_STEPS = [
  [200, 25],
  [50, 10],
  [0, 5],
];

export const pyramidLimit = (longest) => {
  const raw = Math.max(1, longest) * 1.35;
  const [, step] = PYRAMID_STEPS.find(([above]) => raw > above);
  return Math.ceil(raw / step) * step;
};

export const AgePyramid = ({ bands }) => {
  if (!bands?.some((b) => b.male || b.female)) return <Empty>No dates of birth on record.</Empty>;
  const rows = [...bands].reverse();
  const options = baseOptions({ horizontal: true, stacked: true, legend: true });
  // Symmetric scale with ~35% headroom past the longest bar, so its value
  // label lands inside the plot instead of on top of the band labels.
  const longest = Math.max(...bands.map((b) => Math.max(b.male, b.female)));
  const limit = pyramidLimit(longest);
  options.scales.x = {
    ...options.scales.x,
    min: -limit,
    max: limit,
    // Every bar carries its value, and the padded limit would print as an
    // uneven "275 · 100 · 0 · 100 · 200 · 275" — keep the grid, drop the numbers.
    ticks: { display: false },
  };
  options.plugins.tooltip = {
    ...options.plugins.tooltip,
    callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${Math.abs(ctx.parsed.x)}` },
  };
  options.plugins.datalabels = endLabels((v) => (v ? Math.abs(v) : ""));
  const data = {
    labels: rows.map((b) => b.band),
    datasets: [
      // Male bars point left from zero, so their label anchors at the bar's
      // start edge and is pushed further left, outside the bar.
      barDataset("Male", rows.map((b) => -b.male), GENDER_COLORS.male, {
        borderSkipped: "end",
        datalabels: { anchor: "start", align: "start" },
      }),
      barDataset("Female", rows.map((b) => b.female), GENDER_COLORS.female, { borderSkipped: "start" }),
    ],
  };
  return (
    <Frame height={260} label="Employees by age band and gender">
      <Bar data={data} options={options} plugins={PLUGINS} />
    </Frame>
  );
};

/** The old "Employee Head Count" card: current employees, new employees and
 *  attritions per month, as three lines on one axis like the original. */
export const HeadcountChart = ({ trend }) => (
  <MonthLines
    trend={trend}
    label="Employee head count per month"
    perMonth={64}
    series={[
      { label: "Current Employees", color: SERIES.primary, valueOf: (m) => m.headcount },
      { label: "New Employees", color: "#1baf7a", valueOf: (m) => m.hires },
      { label: "Attritions", color: SERIES.secondary, valueOf: (m) => m.leavers },
    ]}
  />
);
